// client/src/pages/seo/SpeisekarteMitBildernDe.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "Bilder in einer Speisekarte sollten Gäste führen, nicht nur die Seite dekorieren",
  },
  {
    id: "why",
    label: "Warum können Bilder in einer digitalen Speisekarte wichtig sein?",
  },
  {
    id: "where",
    label: "Welche Gerichte sollten zuerst Bilder bekommen?",
  },
  {
    id: "quality",
    label: "Welche Fotoqualität braucht eine gute Speisekarte?",
  },
  {
    id: "workflow",
    label: "Wie verwaltet man Bilder in einer digitalen Speisekarte?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren bei Speisekarten mit Bildern?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich als Restaurantinhaber starten?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place hilft, eine Speisekarte mit Bildern zu erstellen",
  },
  {
    id: "faq",
    label: "FAQ zu Speisekarten mit Bildern",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function SpeisekarteMitBildernDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Speisekarte mit Bildern: Restaurantgerichte online besser präsentieren | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/menu-with-pictures",
          de: "/de/speisekarte-mit-bildern",
          no: "/no/meny-med-bilder",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Speisekarte mit Bildern: Restaurantgerichte online besser
              präsentieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Bilder können eine Restaurant-Speisekarte deutlich stärker
                machen. Ein gutes Foto hilft Gästen, ein Gericht schneller zu
                verstehen, Empfehlungen sichtbarer zu machen und neue, saisonale
                oder unbekannte Speisen besser zu präsentieren.
              </p>

              <p className="seoHero__text">
                Bilder sind aber nicht automatisch hilfreich. Schlechte Fotos,
                uneinheitlicher Stil oder zu viele Bilder können eine digitale
                Speisekarte überladen. Entscheidend ist, wo Bilder die
                Entscheidung der Gäste wirklich unterstützen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Bilder in einer Speisekarte sollten Gäste führen, nicht nur die
            Seite dekorieren
          </h2>

          <p>
            Viele Restaurants starten mit der Idee, dass jedes Gericht ein Foto
            braucht. Das klingt logisch, ist aber nicht immer der beste Ansatz.
            In einer langen Speisekarte können zu viele Bilder unruhig wirken
            und die mobile Nutzung verlangsamen.
          </p>

          <p>
            Besser ist ein gezielter Ansatz: Nutzen Sie Bilder dort, wo sie
            Gästen bei der Entscheidung helfen. Zum Beispiel bei Empfehlungen,
            neuen Gerichten, Spezialitäten des Hauses, Desserts, Cocktails oder
            saisonalen Angeboten.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Schwächer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Jedes Gericht bekommt irgendein verfügbares Foto.
                </li>
                <li className="text__seo-li">
                  Unterschiedliche Lichtstimmungen.
                </li>
                <li className="text__seo-li">
                  Dunkle oder unscharfe Bilder.
                </li>
                <li className="text__seo-li">
                  Die Speisekarte wird langsam und überladen.
                </li>
                <li className="text__seo-li">
                  Fotos passen nicht zum echten Gericht.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Bilder zuerst für wichtige Gerichte verwenden.
                </li>
                <li className="text__seo-li">
                  Einen einheitlichen visuellen Stil beibehalten.
                </li>
                <li className="text__seo-li">
                  Helle, klare Fotos nutzen.
                </li>
                <li className="text__seo-li">
                  Mobile Ladegeschwindigkeit beachten.
                </li>
                <li className="text__seo-li">
                  Realistisch zeigen, was der Gast bekommt.
                </li>
              </ul>
            </div>
          </div>

          <h3>Bilder zuerst mit wenigen Gerichten testen</h3>

          <p>
            Starten Sie mit Empfehlungen, Tagesangeboten oder Bestsellern. So
            können Sie leichter prüfen, ob Bilder Ihre digitale Speisekarte
            verbessern, ohne die Seite zu überladen.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum können Bilder in einer digitalen Speisekarte wichtig sein?
            </h2>

            <p>
              Gäste entscheiden nicht nur durch Lesen. Namen, Preise und
              Beschreibungen sind wichtig, aber Bilder kommunizieren oft
              schneller: Portionsgröße, Stil, Zutaten, Präsentation und Stimmung.
            </p>

            <p>
              Bilder sind besonders hilfreich, wenn ein Gericht schwer
              vorstellbar ist, wenn Gäste die Küche nicht gut kennen oder wenn
              das Restaurant bestimmte Angebote hervorheben möchte.
            </p>

            <h3>Bilder können die Entscheidung erleichtern</h3>

            <p>
              Eine Speisekarte mit Bildern ist nicht nur eine Designfrage. Sie
              kann beeinflussen, wie schnell Gäste Gerichte verstehen und welche
              Angebote sie zuerst wahrnehmen.
            </p>

            <p>
              Die stärksten Fotos dekorieren die Speisekarte nicht einfach. Sie
              beantworten eine echte Gästefrage: Wie sieht dieses Gericht aus,
              wie wird es serviert und passt es zu dem, worauf ich gerade Lust
              habe?
            </p>
          </div>
        </section>

        <section id="where" className="acquaintance">
          <h2>Welche Gerichte sollten zuerst Bilder bekommen?</h2>

          <p>
            Nicht jedes Gericht braucht ein Bild. In vielen Fällen ist weniger
            besser. Die stärksten Bilder sind die, die ein konkretes Problem für
            den Gast lösen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Empfehlungen des Hauses.</li>
            <li className="text__seo-li">Tagesangebote.</li>
            <li className="text__seo-li">Saisonale Angebote.</li>
            <li className="text__seo-li">Desserts.</li>
            <li className="text__seo-li">Cocktails und Getränke.</li>
            <li className="text__seo-li">Unbekannte Spezialitäten.</li>
            <li className="text__seo-li">Gerichte für Touristen.</li>
            <li className="text__seo-li">Höherpreisige Gerichte.</li>
          </ul>

          <p>
            Ein Bild ist besonders nützlich, wenn es eine Frage beantwortet: Wie
            sieht das Gericht aus? Wie groß ist die Portion? Ist es leicht,
            deftig, modern oder traditionell?
          </p>

          <VisualIdea>
            Eine digitale Speisekarte mit drei hervorgehobenen Artikeln:
            Tagesempfehlung, Dessert und Cocktail. Alle Fotos haben denselben
            hellen Stil.
          </VisualIdea>
        </section>

        <section id="quality" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Fotoqualität braucht eine gute Speisekarte?</h2>

            <p>
              Gute Menüfotos müssen nicht wie eine große Werbekampagne aussehen.
              Sie sollten aber sauber, hell, realistisch und einheitlich sein.
            </p>

            <p>
              Gäste sollten ungefähr das erwarten können, was sie auf dem Foto
              sehen. Ein Bild, das viel besser aussieht als das echte Gericht,
              kann kurzfristig Aufmerksamkeit erzeugen, aber später Vertrauen
              beschädigen.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Natürliches Licht.</li>
              <li className="text__seo-li">Ruhiger Hintergrund.</li>
              <li className="text__seo-li">Echtes Gericht.</li>
              <li className="text__seo-li">Einheitlicher Bildstil.</li>
              <li className="text__seo-li">Nicht zu viele Filter.</li>
              <li className="text__seo-li">Klare Schärfe.</li>
              <li className="text__seo-li">Mobile Dateigröße.</li>
              <li className="text__seo-li">Realistische Portion.</li>
            </ul>

            <h3>Fotos sind Teil der digitalen Präsentation des Restaurants</h3>

            <p>
              In einer digitalen Speisekarte werden Fotos Teil davon, wie das
              Restaurant wahrgenommen wird. Sie sollten Vertrauen unterstützen
              und keine unrealistischen Erwartungen erzeugen.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>Wie verwaltet man Bilder in einer digitalen Speisekarte?</h2>

          <p>
            Der beste Ablauf ist einfach: Starten Sie nicht mit 80 Fotos.
            Beginnen Sie mit einer kleinen Auswahl und prüfen Sie, ob die Bilder
            innerhalb der Speisekarte wirklich helfen.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Gerichte auswählen:</strong> Starten Sie mit Bestsellern,
              Empfehlungen oder saisonalen Angeboten.
            </li>

            <li className="text__seo-li">
              <strong>Einen visuellen Stil wählen:</strong> Ähnlicher Winkel,
              ähnliches Licht und ein ruhiger Hintergrund.
            </li>

            <li className="text__seo-li">
              <strong>Fotos hochladen:</strong> Verwenden Sie Bilder nur dort,
              wo sie Gästen wirklich helfen.
            </li>

            <li className="text__seo-li">
              <strong>Auf dem Smartphone prüfen:</strong> Stellen Sie sicher,
              dass die Speisekarte schnell lädt und leicht zu überblicken bleibt.
            </li>

            <li className="text__seo-li">
              <strong>Regelmäßig ersetzen:</strong> Saisonale Gerichte und
              Tagesangebote brauchen aktuelle Fotos.
            </li>
          </ol>

          <p>
            Eine digitale Speisekarte ist flexibler als eine gedruckte.
            Bilder können hinzugefügt, ersetzt oder entfernt werden, ohne den
            QR-Code neu zu drucken.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Fehler passieren bei Speisekarten mit Bildern?</h2>

            <p>
              Bilder können helfen, aber sie können auch schaden. Das passiert
              meistens dann, wenn sie ohne klares Ziel hinzugefügt werden.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zu viele Bilder auf einmal.
                  </li>
                  <li className="text__seo-li">
                    Dunkle oder unscharfe Fotos.
                  </li>
                  <li className="text__seo-li">
                    Uneinheitliche Bildstile.
                  </li>
                  <li className="text__seo-li">
                    Zu stark bearbeitete Bilder, die unrealistisch wirken.
                  </li>
                  <li className="text__seo-li">
                    Große Dateien, die die Speisekarte verlangsamen.
                  </li>
                  <li className="text__seo-li">
                    Fotos passen nicht zum echten Gericht.
                  </li>
                  <li className="text__seo-li">
                    Alte Bilder bleiben online, obwohl sich das Gericht geändert
                    hat.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Mit wenigen wichtigen Bildern starten.
                  </li>
                  <li className="text__seo-li">
                    Helle und klare Fotos verwenden.
                  </li>
                  <li className="text__seo-li">
                    Einen einheitlichen Stil beibehalten.
                  </li>
                  <li className="text__seo-li">
                    Gerichte realistisch zeigen.
                  </li>
                  <li className="text__seo-li">
                    Bilder für mobile Nutzung optimieren.
                  </li>
                  <li className="text__seo-li">
                    Fotos regelmäßig prüfen.
                  </li>
                  <li className="text__seo-li">
                    Alte Bilder nach Menüänderungen entfernen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich als Restaurantinhaber starten?</h2>

          <p>
            Ich würde nicht versuchen, sofort jedes Gericht zu fotografieren.
            Das kostet viel Zeit und führt oft zu uneinheitlichen Ergebnissen.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Mit 5 bis 10 Gerichten starten:</strong> Empfehlungen,
              Bestseller oder saisonale Gerichte.
            </li>

            <li className="text__seo-li">
              <strong>Einen Fotostil wählen:</strong> Gleicher Ort, ähnliches
              Licht, ähnlicher Winkel.
            </li>

            <li className="text__seo-li">
              <strong>Auf dem Smartphone testen:</strong> Prüfen Sie, ob die
              Bilder gut aussehen und die Speisekarte nicht überladen wirkt.
            </li>

            <li className="text__seo-li">
              <strong>Eine Woche beobachten:</strong> Fragen Gäste nach diesen
              Gerichten? Lassen sie sich leichter erklären?
            </li>

            <li className="text__seo-li">
              <strong>Später erweitern:</strong> Fügen Sie weitere Bilder nur
              dort hinzu, wo sie wirklich helfen.
            </li>
          </ol>

          <p>
            Ziel ist nicht, ein Fotoarchiv aufzubauen. Ziel ist, Gästen die
            Entscheidung leichter zu machen.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie eine Speisekarte mit Bildern erstellen möchten, starten
              Sie mit einem kleinen, sauberen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Eine digitale Speisekarte erstellen.
              </li>
              <li className="text__seo-li">
                5 bis 10 wichtige Gerichte auswählen.
              </li>
              <li className="text__seo-li">
                Helle, realistische Fotos machen.
              </li>
              <li className="text__seo-li">
                Die Bilder in die Speisekarte hochladen.
              </li>
              <li className="text__seo-li">
                Die Speisekarte auf dem Smartphone öffnen.
              </li>
              <li className="text__seo-li">
                Ladegeschwindigkeit und Lesbarkeit prüfen.
              </li>
              <li className="text__seo-li">
                Weitere Bilder erst nach dem ersten Test hinzufügen.
              </li>
            </ul>

            <VisualIdea>
              Ein kleiner Fototest: fünf ausgewählte Gerichte, ein einheitlicher
              Fotostil und eine Smartphone-Ansicht, die zeigt, ob die
              Speisekarte klar und schnell bleibt.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zu Speisekarten mit Bildern</h2>

          <div className="seoFaq">
            <details>
              <summary>Sollte jedes Gericht ein Bild haben?</summary>
              <p>
                Nicht unbedingt. Oft ist es besser, Bilder gezielt für
                Empfehlungen, Bestseller, Tagesangebote oder schwer vorstellbare
                Gerichte zu verwenden.
              </p>
            </details>

            <details>
              <summary>Kann ich Menüfotos mit einem Smartphone machen?</summary>
              <p>
                Ja, wenn Licht, Schärfe und Stil gut sind. Das Foto sollte
                realistisch zeigen, was der Gast erwarten kann.
              </p>
            </details>

            <details>
              <summary>Können Bilder die Speisekarte langsamer machen?</summary>
              <p>
                Große und nicht optimierte Bilder können eine digitale
                Speisekarte verlangsamen. Bilder sollten sorgfältig ausgewählt
                und für mobile Nutzung optimiert werden.
              </p>
            </details>

            <details>
              <summary>Sind Bilder besser als Beschreibungen?</summary>
              <p>
                Nein. Gute Bilder und kurze Beschreibungen arbeiten zusammen.
                Das Bild erzeugt den ersten Eindruck, während die Beschreibung
                Zutaten, Stil oder Details erklärt.
              </p>
            </details>

            <details>
              <summary>Welche Gerichte sollte ich zuerst fotografieren?</summary>
              <p>
                Starten Sie mit Bestsellern, Empfehlungen des Hauses,
                saisonalen Gerichten, Desserts oder Gerichten, die Gäste
                vielleicht noch nicht gut kennen.
              </p>
            </details>

            <details>
              <summary>Kann ich Bilder später ersetzen?</summary>
              <p>
                Ja. In einer digitalen Speisekarte können Fotos ersetzt,
                entfernt oder hinzugefügt werden, ohne den QR-Code neu zu
                drucken.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/speisekarte-mit-bildern"
        />
      </article>
    </main>
  );
}